import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { MultiValue } from "react-select";

import { supportRequestService } from "@/app/services/supportRequestService";
import { ListMySupportRequestsParams } from "@/app/services/supportRequestService/listAllSupport";
import { useDebounce } from "@/app/hooks/useDebounce";
import { useAuth } from "@/app/hooks/useAuth";
import { useToast } from "@/view/components/ui/use-toast";
import { OptionType } from "@/app/utils/optionSelectType";
import {
  optionsSupportRequestReasonSelect,
  optionsSupportRequestStatusSelect,
  SUPPORT_STATUS_LABEL,
  SupportRequestReason,
  SupportRequestStatusEnum,
} from "@/app/entities/SupportRequest";
import {
  DEFAULT_STAFF_STATUS_FILTER,
  getStatusListOrder,
} from "@/app/utils/supportRequest";
import { SupportRequestColumnsDefinition } from "./components/SupportRequestColumnsDefinition";

const LIMIT = 15;

const DEFAULT_STAFF_STATUS_OPTIONS: OptionType[] =
  DEFAULT_STAFF_STATUS_FILTER.map((status) => ({
    label: SUPPORT_STATUS_LABEL[status],
    value: status,
  }));

export function useSupportRequests() {
  const { isSuperAdmin, isAdmin, isSupport } = useAuth();
  const isStaffView = isSuperAdmin || isAdmin || isSupport;
  const queryClient = useQueryClient();

  const [searchValue, setSearchValue] = useState("");
  const [selectedStatusOptions, setSelectedStatusOptions] = useState<
    MultiValue<OptionType>
  >(isStaffView ? DEFAULT_STAFF_STATUS_OPTIONS : []);
  const [selectedReasonOptions, setSelectedReasonOptions] = useState<
    MultiValue<OptionType>
  >([]);
  const [params, setParams] = useState<
    Omit<ListMySupportRequestsParams, "skip" | "limit">
  >(isStaffView ? { status: DEFAULT_STAFF_STATUS_FILTER } : {});

  const [selectedNumber, setSelectedNumber] = useState<string | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const observerRef = useRef<HTMLDivElement>(null);

  const debouncedSearchValue = useDebounce(500, searchValue);
  const { toast } = useToast();

  useEffect(() => {
    setParams((prev) => ({
      ...prev,
      search: debouncedSearchValue || undefined,
    }));
  }, [debouncedSearchValue]);

  const {
    data,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
  } = useInfiniteQuery({
    queryKey: ["mySupportRequests", params],
    queryFn: ({ pageParam = 0 }) =>
      supportRequestService.listSupportRequests({
        ...params,
        skip: pageParam,
        limit: LIMIT,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextSkip = lastPage.pagination.skip + lastPage.pagination.limit;
      if (nextSkip >= lastPage.pagination.total) return undefined;
      return nextSkip;
    },
  });

  const supportRequests = useMemo(() => {
    const list = data?.pages.flatMap((page) => page.supportRequests) ?? [];

    if (isStaffView) return list;

    return [...list].sort(
      (a, b) => getStatusListOrder(a.status) - getStatusListOrder(b.status),
    );
  }, [data, isStaffView]);

  const shouldShowInfiniteScroll = Boolean(hasNextPage);

  useEffect(() => {
    if (
      !observerRef.current ||
      !shouldShowInfiniteScroll ||
      isFetchingNextPage
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "200px",
      },
    );

    observer.observe(observerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    shouldShowInfiniteScroll,
    supportRequests.length,
  ]);

  const { mutateAsync: claimNextSupportRequest, isPending: isClaimingNext } =
    useMutation({
      mutationFn: () => supportRequestService.claimNextSupportRequest(),
      onSuccess: (data) => {
        if (!data) {
          toast({
            variant: "default",
            title: "Não há chamados em aberto no momento",
          });
          return;
        }
        queryClient.invalidateQueries({ queryKey: ["mySupportRequests"] });
        openDetails(data?.number || "");
      },
      onError: () => {
        toast({
          variant: "destructive",
          title: "Erro ao assumir chamado, tente novamente mais tarde!",
        });
      },
    });

  const { columns } = SupportRequestColumnsDefinition(isStaffView);

  const table = useReactTable({
    data: supportRequests,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    if (isError) {
      toast({
        variant: "destructive",
        title: "Erro ao carregar os chamados, tente novamente mais tarde!",
      });
    }
  }, [isError, toast]);

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleSelectStatus = (options: MultiValue<OptionType>) => {
    setSelectedStatusOptions(options);
    setParams((prev) => ({
      ...prev,
      status:
        options.length > 0
          ? options.map((option) => option.value as SupportRequestStatusEnum)
          : undefined,
    }));
  };

  const handleSelectReason = (options: MultiValue<OptionType>) => {
    setSelectedReasonOptions(options);
    setParams((prev) => ({
      ...prev,
      reason:
        options.length > 0
          ? options.map((option) => option.value as SupportRequestReason)
          : undefined,
    }));
  };

  const handleClearFilters = () => {
    setSelectedStatusOptions([]);
    setSelectedReasonOptions([]);
    setParams((prev) => ({
      ...prev,
      status: undefined,
      reason: undefined,
    }));
  };

  const openDetails = useCallback((number: string) => {
    setSelectedNumber(number);
    setIsDetailsModalOpen(true);
  }, []);

  const closeDetails = useCallback(() => {
    setIsDetailsModalOpen(false);
    setSelectedNumber(null);
  }, []);

  return {
    pageTitle: isStaffView ? "Chamados" : "Meus chamados",
    isStaffView,

    table,
    supportRequests,
    isLoading,
    isFetching: isFetching && !isFetchingNextPage,
    isFetchingNextPage,
    observerRef,
    shouldShowInfiniteScroll,

    searchValue,
    handleSearchInputChange,

    statusOptions: optionsSupportRequestStatusSelect,
    selectedStatusOptions,
    handleSelectStatus,

    reasonOptions: optionsSupportRequestReasonSelect,
    selectedReasonOptions,
    handleSelectReason,

    handleClearFilters,

    selectedNumber,
    isDetailsModalOpen,
    openDetails,
    closeDetails,

    isClaimingNext,
    claimNextSupportRequest,
  };
}
